// acta_patch_commercial_visibility_gmao_map_v1
// acta_patch_lot3_quotes_foundation_v1
// acta_patch_lot2_clients_machine_catalog_v1
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Documents from "@/pages/Documents";
import CommercePortfolio from "@/pages/CommercePortfolio";
import ProspectDetail from "@/pages/ProspectDetail";
import Stats from "@/pages/Stats";
import MapPage from "@/pages/MapPage";
import Supervision from "@/pages/Supervision";
import MonitoringLogin from "@/pages/MonitoringLogin";
import Settings from "@/pages/Settings";
import SupportTickets from "@/pages/SupportTickets";
import { TenantBrandingProvider } from "@/context/TenantBrandingContext";
import {
  TenantFeaturesProvider,
  useTenantFeatures,
} from "@/context/TenantFeaturesContext";
import {
  hasModuleAccess,
  hasPageAccess,
  hasAnyRole,
} from "@/lib/accessPermissions";
import PublicShare from "@/pages/PublicShare";
import PublicSav from "@/pages/PublicSav";
import RGPD from "@/pages/legal/RGPD";
import CGU from "@/pages/legal/CGU";
import MentionsLegales from "@/pages/legal/MentionsLegales";
import Confidentialite from "@/pages/legal/Confidentialite";
import CookieBanner from "@/components/CookieBanner";
import ChangePassword from "@/pages/ChangePassword";
import CommerceDashboard from "@/pages/CommerceDashboard";
import CommercePipeline from "@/pages/CommercePipeline";
import CommerceQuotes from "@/pages/CommerceQuotes";
import CommerceAgenda from "@/pages/CommerceAgenda";
import CommerceWorkshops from "@/pages/CommerceWorkshops";
import CommerceWorkshopDetail from "@/pages/CommerceWorkshopDetail";
import GmaoInstallationDetail from "@/pages/GmaoInstallationDetail";
import GmaoMap from "@/pages/GmaoMap";
import GmaoMaintenance from "@/pages/GmaoMaintenance";
import GmaoInterventionDetail from "@/pages/GmaoInterventionDetail";
import GmaoSavTickets from "@/pages/GmaoSavTickets";
import GmaoSavTicketDetail from "@/pages/GmaoSavTicketDetail";
import GmaoPartDetail from "@/pages/GmaoPartDetail";
import Mail from "@/pages/MailWithAffairAssociation";
import AcquisitionDashboard from "@/pages/AcquisitionDashboard";
import AcquisitionLeads from "@/pages/AcquisitionLeads";
import AcquisitionLeadDetail from "@/pages/AcquisitionLeadDetail";
import AcquisitionCampaigns from "@/pages/AcquisitionCampaigns";
import AcquisitionCampaignDetail from "@/pages/AcquisitionCampaignDetail";
import AcquisitionConnections from "@/pages/AcquisitionConnections";
import AcquisitionSettings from "@/pages/AcquisitionSettings";

import GmaoDashboardV2 from "@/pages/gmao-v2/GmaoDashboardV2";
import GmaoParcV2 from "@/pages/gmao-v2/GmaoParcV2";
import GmaoMachineDetailV2 from "@/pages/gmao-v2/GmaoMachineDetailV2";
import GmaoPlanningV2 from "@/pages/gmao-v2/GmaoPlanningV2";
import GmaoInstallationsV2 from "@/pages/gmao-v2/GmaoInstallationsV2";

const COMMERCIAL_ACCESS_ROUTES = [
  ["dashboard", "/app/commerce/dashboard"],
  ["pipeline", "/app/commerce/pipeline"],
  ["quotes", "/app/commerce/quotes"],
  ["agenda", "/app/commerce/agenda"],
  ["workshops", "/app/commerce/workshops"],
  ["portfolio", "/app/commerce/prospects"],
  ["stats", "/app/commerce/stats"],
  ["map", "/app/commerce/map"],
];

const ACQUISITION_ACCESS_ROUTES = [
  ["dashboard", "/app/acquisition/dashboard"],
  ["leads", "/app/acquisition/leads"],
  ["campaigns", "/app/acquisition/campaigns"],
  ["connections", "/app/acquisition/connections"],
  ["settings", "/app/acquisition/settings"],
];

const GMAO_ACCESS_ROUTES = [
  ["dashboard", "/app/gmao/v2/dashboard"],
  ["installations", "/app/gmao/v2/installations"],
  ["sav", "/app/gmao/sav"],
  ["planning", "/app/gmao/v2/planning"],
  ["maintenance", "/app/gmao/maintenance"],
  ["machines", "/app/gmao/v2/parc"],
  ["parts", "/app/gmao/v2/parc?type=parts"],
];

function firstAccessiblePagePath(
  user,
  module,
  routes
) {
  if (
    !hasModuleAccess(
      user,
      module
    )
  ) {
    return null;
  }

  return routes.find(([page]) =>
    hasPageAccess(user, module, page)
  )?.[1] || null;
}

function ModuleHomeRedirect({
  module,
  routes,
}) {
  const { user } = useAuth();
  const destination = firstAccessiblePagePath(
    user,
    module,
    routes
  );

  return (
    <Navigate
      to={destination || "/app"}
      replace
    />
  );
}

function LegacyMachineRedirect() {
  const { machineId } = useParams();
  return (
    <Navigate
      to={`/app/gmao/v2/parc/${encodeURIComponent(machineId)}`}
      replace
    />
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  if (user === null) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="label-eyebrow lirah-pulse">Chargement</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (user.mustChangePassword === true && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to={user.mustChangePassword ? "/change-password" : "/app"} replace />;
  return children;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();

  // Pendant le chargement de /auth/me, on évite un affichage temporaire incorrect.
  if (user === null) return null;

  // Sécurité supplémentaire : si l'utilisateur n'est pas connecté.
  if (!user) return <Navigate to="/login" replace />;

  // Si le rôle n'est pas autorisé, on retourne au dashboard.
  if (!hasAnyRole(user, roles)) return <Navigate to="/app" replace />;

  return children;
}

function RequireMailAccess({
  children,
}) {
  const { user } = useAuth();

  if (user === null) return null;

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  if (
    !hasPageAccess(
      user,
      "mail",
      "mail"
    )
  ) {
    return (
      <Navigate
        to="/app"
        replace
      />
    );
  }

  return children;
}


function RequireUserAccess({
  module,
  page,
  children,
}) {
  const { user } = useAuth();

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;

  const allowed = page
    ? hasPageAccess(user, module, page)
    : hasModuleAccess(user, module);

  if (!allowed) return <Navigate to="/app" replace />;

  return children;
}


function RequireFeature({
  feature,
  children,
}) {
  const {
    loading,
    hasFeature,
  } = useTenantFeatures();

  if (loading) {
    return null;
  }

  if (!hasFeature(feature)) {
    return (
      <Navigate
        to="/app"
        replace
      />
    );
  }

  return children;
}


function AppHome() {
  const { user } = useAuth();

  const {
    loading,
    hasFeature,
  } = useTenantFeatures();

  if (loading) {
    return null;
  }

  if (user?.role === "saas_admin") {
    return (
      <Navigate
        to="/monpremiermonitoring.fr/dashboard"
        replace
      />
    );
  }

  const commercialHome =
    hasFeature("commercialEnabled")
      ? firstAccessiblePagePath(
          user,
          "commercial",
          COMMERCIAL_ACCESS_ROUTES
        )
      : null;

  const gmaoHome =
    hasFeature("gmaoEnabled")
      ? firstAccessiblePagePath(
          user,
          "gmao",
          GMAO_ACCESS_ROUTES
        )
      : null;

  const documentsHome =
    hasFeature("documentsEnabled")
    && hasPageAccess(
      user,
      "documents",
      "documents"
    )
      ? "/app/documents"
      : null;

  const gmaoFirst = [
    "maintenance_manager",
    "technician",
  ].includes(user?.role);

  const destination = gmaoFirst
    ? (
        gmaoHome
        || commercialHome
        || documentsHome
        || "/app/settings"
      )
    : (
        commercialHome
        || documentsHome
        || gmaoHome
        || "/app/settings"
      );

  return (
    <Navigate
      to={destination}
      replace
    />
  );
}


export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors theme="system" />
            <CookieBanner />
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
              <Route path="/register" element={<Navigate to="/login" replace />} />
              <Route path="/share/:token" element={<PublicShare />} />
              <Route path="/sav/:token" element={<PublicSav />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/about" element={<Navigate to="/login" replace />} />
              <Route path="/contact" element={<Navigate to="/login" replace />} />
              <Route path="/legal/rgpd" element={<RGPD />} />
              <Route path="/legal/cgu" element={<CGU />} />
              <Route path="/legal/mentions" element={<MentionsLegales />} />
              <Route path="/legal/confidentialite" element={<Confidentialite />} />
                <Route path="/monpremiermonitoring.fr" element={<MonitoringLogin />} />
              <Route
                path="/monpremiermonitoring.fr/dashboard"
                element={
                  <Protected>
                    <TenantBrandingProvider>
                      <AppLayout>
                        <RequireRole roles={["saas_admin"]}>
                          <Supervision />
                        </RequireRole>
                      </AppLayout>
                    </TenantBrandingProvider>
                  </Protected>
                }
              />
                <Route
                path="/app"
                element={
                  <Protected>
                    <TenantBrandingProvider>
                      <TenantFeaturesProvider>
                        <AppLayout />
                      </TenantFeaturesProvider>
                    </TenantBrandingProvider>
                  </Protected>
                }
              >
                <Route index element={<AppHome />} />
                <Route
                  path="commerce"
                  element={
                    <ModuleHomeRedirect
                      module="commercial"
                      routes={COMMERCIAL_ACCESS_ROUTES}
                    />
                  }
                />
                <Route
                  path="commerce/dashboard"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="dashboard"
                    >
                      <RequireRole
                      roles={[
                        "admin",
                        "manager",
                        "user",
                        "maintenance_manager",
                      ]}
                    >
                      <CommerceDashboard />
                    </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/pipeline"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="pipeline"
                    >
                      <RequireRole
                      roles={[
                        "admin",
                        "manager",
                        "user",
                        "maintenance_manager",
                      ]}
                    >
                      <CommercePipeline />
                    </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/quotes"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="quotes"
                    >
                      <RequireRole
                      roles={[
                        "admin",
                        "manager",
                        "user",
                        "maintenance_manager",
                      ]}
                    >
                      <CommerceQuotes />
                    </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/agenda"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="agenda"
                    >
                      <RequireRole
                      roles={[
                        "admin",
                        "manager",
                        "user",
                        "maintenance_manager",
                      ]}
                    >
                      <CommerceAgenda />
                    </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/workshops"
                  element={
                    <RequireUserAccess module="commercial" page="workshops">
                      <RequireRole roles={["admin", "manager", "user"]}>
                        <CommerceWorkshops />
                      </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/workshops/:id"
                  element={
                    <RequireUserAccess module="commercial" page="workshops">
                      <RequireRole roles={["admin", "manager", "user"]}>
                        <CommerceWorkshopDetail />
                      </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/prospects"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="portfolio"
                    >
                      <RequireRole
                      roles={[
                        "admin",
                        "manager",
                        "user",
                        "maintenance_manager",
                      ]}
                    >
                      <CommercePortfolio />
                    </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/clients"
                  element={<Navigate to="/app/commerce/prospects" replace />}
                />
                
                <Route
                  path="commerce/prospects/:id"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="portfolio"
                    >
                      <RequireRole
                      roles={[
                        "admin",
                        "manager",
                        "user",
                        "maintenance_manager",
                      ]}
                    >
                      <ProspectDetail />
                    </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/stats"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="stats"
                    >
                      <RequireRole
                      roles={[
                        "admin",
                        "manager",
                        "user",
                        "maintenance_manager",
                      ]}
                    >
                      <Stats />
                    </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="commerce/map"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="map"
                    >
                      <RequireRole
                        roles={[
                          "admin",
                          "manager",
                          "user",
                          "maintenance_manager",
                        ]}
                      >
                        <MapPage />
                      </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="mail"
                  element={
                    <RequireMailAccess>
                      <RequireRole
                        roles={[
                          "admin",
                          "manager",
                          "user",
                          "maintenance_manager",
                          "technician",
                        ]}
                      >
                        <Mail />
                      </RequireRole>
                    </RequireMailAccess>
                  }
                />
                <Route
                  path="acquisition"
                  element={
                    <RequireFeature feature="acquisitionEnabled">
                      <RequireRole roles={["admin"]}>
                        <ModuleHomeRedirect
                          module="acquisition"
                          routes={ACQUISITION_ACCESS_ROUTES}
                        />
                      </RequireRole>
                    </RequireFeature>
                  }
                />
                <Route
                  path="acquisition/dashboard"
                  element={
                    <RequireFeature feature="acquisitionEnabled">
                      <RequireUserAccess module="acquisition" page="dashboard">
                        <RequireRole roles={["admin"]}>
                          <AcquisitionDashboard />
                        </RequireRole>
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />
                <Route
                  path="acquisition/leads"
                  element={
                    <RequireFeature feature="acquisitionEnabled">
                      <RequireUserAccess module="acquisition" page="leads">
                        <RequireRole roles={["admin"]}>
                          <AcquisitionLeads />
                        </RequireRole>
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />
                <Route
                  path="acquisition/leads/:leadId"
                  element={
                    <RequireFeature feature="acquisitionEnabled">
                      <RequireUserAccess module="acquisition" page="leads">
                        <RequireRole roles={["admin"]}>
                          <AcquisitionLeadDetail />
                        </RequireRole>
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />
                <Route
                  path="acquisition/campaigns"
                  element={
                    <RequireFeature feature="acquisitionEnabled">
                      <RequireUserAccess module="acquisition" page="campaigns">
                        <RequireRole roles={["admin"]}>
                          <AcquisitionCampaigns />
                        </RequireRole>
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />
                <Route
                  path="acquisition/campaigns/:campaignId"
                  element={
                    <RequireFeature feature="acquisitionEnabled">
                      <RequireUserAccess module="acquisition" page="campaigns">
                        <RequireRole roles={["admin"]}>
                          <AcquisitionCampaignDetail />
                        </RequireRole>
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />
                <Route
                  path="acquisition/connections"
                  element={
                    <RequireFeature feature="acquisitionEnabled">
                      <RequireUserAccess module="acquisition" page="connections">
                        <RequireRole roles={["admin"]}>
                          <AcquisitionConnections />
                        </RequireRole>
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />
                <Route
                  path="acquisition/settings"
                  element={
                    <RequireFeature feature="acquisitionEnabled">
                      <RequireUserAccess module="acquisition" page="settings">
                        <RequireRole roles={["admin"]}>
                          <AcquisitionSettings />
                        </RequireRole>
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />

                <Route
                  path="gmao"
                  element={
                    <ModuleHomeRedirect
                      module="gmao"
                      routes={GMAO_ACCESS_ROUTES}
                    />
                  }
                />
                <Route
                  path="gmao/dashboard"
                  element={
                    <Navigate
                      to="/app/gmao/v2/dashboard"
                      replace
                    />
                  }
                />
                <Route
                  
                  path="gmao/v2/dashboard"
                  element={
                    <RequireFeature feature="gmaoEnabled">
                      <RequireUserAccess module="gmao" page="dashboard">
                        <GmaoDashboardV2 />
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />

                <Route
                  path="gmao/v2/parc"
                  element={
                    <RequireFeature feature="gmaoEnabled">
                      <RequireUserAccess module="gmao" page="machines">
                        <GmaoParcV2 />
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />


                <Route
                  path="gmao/v2/parc/:machineId"
                  element={
                    <RequireFeature feature="gmaoEnabled">
                      <RequireUserAccess module="gmao" page="machines">
                        <GmaoMachineDetailV2 />
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />

                <Route
                  path="gmao/v2/planning"
                  element={
                    <RequireFeature feature="gmaoEnabled">
                      <RequireUserAccess module="gmao" page="planning">
                        <GmaoPlanningV2 />
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />

                <Route
                  path="gmao/v2/installations"
                  element={
                    <RequireFeature feature="gmaoEnabled">
                      <RequireUserAccess module="gmao" page="installations">
                        <GmaoInstallationsV2 />
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />

                  <Route
                    path="gmao/installations"
                    element={
                      <Navigate
                        to="/app/gmao/v2/installations"
                        replace
                      />
                    }
                  />
                <Route
                  path="gmao/installations/:installationId"
                  element={
                    <RequireFeature feature="gmaoEnabled">
                      <RequireUserAccess
                        module="gmao"
                        page="installations"
                      >
                        <RequireRole
                          roles={[
                            "admin",
                            "maintenance_manager",
                          ]}
                        >
                          <GmaoInstallationDetail />
                        </RequireRole>
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />
                <Route
                  path="gmao/map"
                  element={
                    <RequireFeature feature="gmaoEnabled">
                      <RequireUserAccess module="gmao" page="machines">
                        <GmaoMap />
                      </RequireUserAccess>
                    </RequireFeature>
                  }
                />
                <Route
                  path="gmao/maintenance"
                  element={
                      <RequireFeature feature="gmaoEnabled">
                        <RequireUserAccess
                          module="gmao"
                          page="maintenance"
                        >
                          <GmaoMaintenance />
                        </RequireUserAccess>
                      </RequireFeature>
                  }
                />
                <Route
                  path="gmao/sav"
                  element={
                      <RequireFeature feature="gmaoEnabled">
                        <RequireUserAccess
                          module="gmao"
                          page="sav"
                        >
                          <GmaoSavTickets />
                        </RequireUserAccess>
                      </RequireFeature>
                  }
                />
                <Route
                  path="gmao/sav/:ticketId"
                  element={
                      <RequireFeature feature="gmaoEnabled">
                        <RequireUserAccess
                          module="gmao"
                          page="sav"
                        >
                          <GmaoSavTicketDetail />
                        </RequireUserAccess>
                      </RequireFeature>
                  }
                />
                <Route
                  path="gmao/planning"
                  element={<Navigate to="/app/gmao/v2/planning" replace />}
                />
                <Route
                  path="gmao/interventions"
                  element={<Navigate to="/app/gmao/v2/planning" replace />}
                />
                <Route
                  path="gmao/interventions/:interventionId"
                  element={
                      <RequireFeature feature="gmaoEnabled">
                        <RequireUserAccess
                          module="gmao"
                          page="interventions"
                        >
                          <GmaoInterventionDetail />
                        </RequireUserAccess>
                      </RequireFeature>
                  }
                />
                <Route
                  path="gmao/parts"
                  element={<Navigate to="/app/gmao/v2/parc?type=parts" replace />}
                />
                <Route
                  path="gmao/parts/:partId"
                  element={
                      <RequireFeature feature="gmaoEnabled">
                        <RequireUserAccess
                          module="gmao"
                          page="parts"
                        >
                          <GmaoPartDetail />
                        </RequireUserAccess>
                      </RequireFeature>
                  }
                />
                <Route
                  path="gmao/machines"
                  element={<Navigate to="/app/gmao/v2/parc" replace />}
                />
                <Route
                  path="gmao/machines/:machineId"
                  element={<LegacyMachineRedirect />}
                />
                <Route
                  path="documents"
                  element={
                    <RequireUserAccess
                      module="documents"
                      page="documents"
                    >
                      <Documents />
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="prospects"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="portfolio"
                    >
                      <RequireRole roles={["admin", "manager", "user", "maintenance_manager"]}>
                        <CommercePortfolio />
                      </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="prospects/:id"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="portfolio"
                    >
                      <RequireRole roles={["admin", "manager", "user", "maintenance_manager"]}>
                        <ProspectDetail />
                      </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route path="users" element={<Navigate to="/app/settings?section=users" replace />} />
                <Route
                  path="stats"
                  element={
                    <RequireUserAccess
                      module="commercial"
                      page="stats"
                    >
                      <RequireRole roles={["admin", "manager", "user", "maintenance_manager"]}>
                        <Stats />
                      </RequireRole>
                    </RequireUserAccess>
                  }
                />
                <Route
                  path="map"
                  element={
                    <Navigate
                      to="/app/commerce/map"
                      replace
                    />
                  }
                />
                <Route path="billing" element={<Navigate to="/app/settings" replace />} />
                <Route path="billing/success" element={<Navigate to="/app/settings" replace />} />
                <Route path="settings" element={<Settings />} />
                <Route path="support" element={<SupportTickets />} />
                <Route
                  path="settings/machines"
                  element={<Navigate to="/app/settings?section=machine_catalog" replace />}
                />
                <Route
                  path="settings/engagement"
                  element={<Navigate to="/app/settings" replace />}
                />
                <Route path="settings/notifications" element={<Navigate to="/app/settings?section=notifications" replace />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}
